// Copyright (c) Microsoft. All rights reserved.

import {
    Link,
    Persona,
    Text,
    makeStaticStyles,
    makeStyles,
    mergeClasses,
    shorthands,
} from '@fluentui/react-components';
import { ThumbDislike24Filled, ThumbLike16Filled } from '@fluentui/react-icons';
import React from 'react';
import { GetResponseOptions, useChat } from '../../../libs/hooks/useChat';
import {
    AuthorRoles,
    ChatMessageType,
    IChatMessage,
    IChatMessageGroupedAttachment,
    IChatSearchQuery,
    UserFeedback,
} from '../../../libs/models/ChatMessage';
import { useAppSelector } from '../../../redux/app/hooks';
import { RootState } from '../../../redux/app/store';
import { FeatureKeys } from '../../../redux/features/app/AppState';
import { ChatScope } from '../../../redux/features/conversations/ChatState';
import { Breakpoints, customTokens } from '../../../styles';
import { mapPlatformTypeColumns } from '../../utils/Mappers';
import { timestampToDateString } from '../../utils/TextUtils';
import { JsonTable } from '../json-table/JsonTable';
import { PlanViewer } from '../plan-viewer/PlanViewer';
import { TypingIndicator } from '../typing-indicator/TypingIndicator';
import * as utils from './../../utils/TextUtils';
import { ChatContextMenu } from './ChatContextMenu';
import { ChatDownloadReport } from './ChatDownloadReport';
import { ChatHistoryDocumentContent } from './ChatHistoryDocumentContent';
import { ChatHistoryTextContent } from './ChatHistoryTextContent';
import { Citations } from './Citations';
import { GroupedAttachments } from './GroupedAttachments';
import { UserFeedbackActions } from './UserFeedbackActions';

const useClasses = makeStyles({
    root: {
        display: 'flex',
        flexDirection: 'row',
        maxWidth: '75%',
        ...shorthands.borderRadius(customTokens.borderRadiusMedium),
        ...Breakpoints.small({
            maxWidth: '100%',
        }),
        ...shorthands.gap(customTokens.spacingHorizontalXS),
        '& img': {
            objectFit: 'cover',
        },
    },
    debug: {
        position: 'absolute',
        top: '-4px',
        right: '-4px',
    },
    alignEnd: {
        alignSelf: 'flex-end',
    },
    persona: {
        paddingTop: customTokens.spacingVerticalS,
    },
    item: {
        backgroundColor: customTokens.colorNeutralBackground1,
        ...shorthands.borderRadius(customTokens.borderRadiusMedium),
        ...shorthands.padding(customTokens.spacingVerticalS, customTokens.spacingHorizontalL),
    },
    me: {
        backgroundColor: customTokens.colorBrandBackground2,
    },
    time: {
        color: customTokens.colorNeutralForeground3,
        fontSize: customTokens.fontSizeBase200,
        fontWeight: 400,
    },
    header: {
        position: 'relative',
        display: 'flex',
        flexDirection: 'row',
        ...shorthands.gap(customTokens.spacingHorizontalL),
    },
    archiveTable: {
        '& > table': {
            width: '100%',
            borderCollapse: 'collapse',
            marginTop: '10px',
            marginBottom: '20px',
            '& th': {
                backgroundColor: 'rgb(25, 133, 151)',
                color: 'white',
                textAlign: 'left',
                ...shorthands.borderBottom('1px', 'solid', '#eee'),
                ...shorthands.padding('8px'),
            },
            '& tr:last-child th': {
                ...shorthands.borderRadius('0px', '0px', '0px', '5px'),
            },
            '& tr:last-child td': {
                ...shorthands.borderRadius('0px', '0px', '5px', '0px'),
            },
            '& td': {
                backgroundColor: '#cccccc',
                ...shorthands.borderBottom('1px', 'solid', '#eeeeee'),
                ...shorthands.padding('8px'),
            },
        },
        '& > table:nth-child(2)': {
            marginTop: '10px',
            marginBottom: '20px',
        },
    },
    canvas: {
        width: '100%',
        textAlign: 'center',
    },
    image: {
        maxWidth: '250px',
    },
    blur: {
        filter: 'blur(5px)',
    },
    tagsContainer: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'flex-start',
        marginTop: customTokens.spacingVerticalXL,
        marginBottom: customTokens.spacingVerticalXL,
        flexWrap: 'wrap',
    },
    tagWrapper: {},
    pill: {
        ...shorthands.borderRadius(customTokens.borderRadiusCircular),
        backgroundColor: 'rgb(25, 133, 151)',
        lineHeight: customTokens.lineHeightBase100,
        ...shorthands.padding(customTokens.spacingVerticalXXS, customTokens.spacingHorizontalS),
        marginRight: customTokens.spacingHorizontalS,
        fontSize: customTokens.fontSizeBase200,
    },
    link: {
        color: customTokens.colorBrandBackgroundInverted,
        ':hover': {
            color: customTokens.colorBrandBackgroundInverted,
        },
    },
    highlight: {
        backgroundColor: 'rgb(61, 174, 94)',
    },
    noUrl: {
        cursor: 'default',
        textDecorationLine: 'none',
        ':hover': {
            textDecorationLine: 'none',
        },
        ':active': {
            textDecorationLine: 'none',
            color: customTokens.colorBrandBackgroundInverted,
        },
    },
});

const useStaticStyles = makeStaticStyles({
    '.contextCitation': {
        borderBottomWidth: '2px',
        borderBottomStyle: 'solid',
        borderBottomColor: 'transparent',
        textDecorationStyle: 'dotted',
        textDecorationColor: '#919191',
        textUnderlineOffset: '3px',
        color: 'inherit',
    },
    '.contextCitation:hover': {
        cursor: 'pointer',
        background: '#fff4f4',
        color: '#692b61',
        textDecorationColor: '#692b61',
        borderRadius: '2px',
    },
    '.contextCitationSup': {
        position: 'relative',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        verticalAlign: 'text-top',
        top: '1px',
        margin: '0px 2px',
        minWidth: '14px',
        height: '14px',
        textDecorationColor: 'transparent',
        outline: 'transparent solid 1px',
        color: '#75306c',
        background: '#f6d0d0',
        borderRadius: '3px',
        fontSize: '10px',
        lineHeight: '14px',
        fontWeight: '600',
        padding: '0px 1px 0px 0px',
    },
    '.archiveDataLink': {
        fontWeight: '600',
        textDecoration: 'underline',
        color: 'rgb(15, 101, 110)',
        cursor: 'pointer',
    },
    '.archiveDataLink:hover': {
        color: 'rgb(188, 98, 126)',
    },
});

interface ChatHistoryItemProps {
    message: IChatMessage;
    getResponse: (options: GetResponseOptions) => Promise<void>;
    messageIndex: number;
}

export const ChatHistoryItem: React.FC<ChatHistoryItemProps> = ({ message, getResponse, messageIndex }) => {
    const classes = useClasses();

    useStaticStyles();

    const chat = useChat();
    const { conversations, selectedId } = useAppSelector((state: RootState) => state.conversations);
    const { activeUserInfo, features } = useAppSelector((state: RootState) => state.app);
    const [archiveMemoryDetails, setArchiveMemoryDetails] = React.useState<Array<Record<string, string>>>([]);
    const [contentWithText, setContentWithText] = React.useState<string[]>([]);

    const isMe = message.authorRole === AuthorRoles.User && message.userId === activeUserInfo?.id;
    const isBot = message.authorRole === AuthorRoles.Bot;
    const user = chat.getChatUserById(message.userName, selectedId, conversations[selectedId].users);
    const fullName = user?.fullName ?? message.userName;

    const avatar = isBot
        ? { image: { src: conversations[selectedId].botProfilePicture } }
        : { name: fullName, color: 'brand' as const };

    const commonColumns = ['System ID', 'Platform', 'Type'];

    const getArchiveMemoryDetails = async (systemIds: string[], contentWithJson: Array<Record<string, string>>) => {
        const filter = systemIds.map((s) => `SystemID eq '${s}'`).join(' or ');

        const searchQuery: IChatSearchQuery = {
            query: '',
            filter,
            useSemanticSearch: false,
            semanticConfigName: '',
        };

        const archiveMemoryDetailsResponse = await chat.getArchiveMemoryDetails(searchQuery);

        if (archiveMemoryDetailsResponse.length > 0) {
            const archiveMemoryDetailsJson = archiveMemoryDetailsResponse.map(
                (item) =>
                    JSON.parse(item.replaceAll('\\"', '&qoute;').replaceAll('\\', '\\\\')) as Array<
                        Record<string, string>
                    >,
            );

            const archiveMemoryDetailsArray = JSON.parse(JSON.stringify(archiveMemoryDetailsJson)) as Array<
                Array<Record<string, string>>
            >;

            const archiveMemoryWithSelectedColumns = archiveMemoryDetailsArray.map(
                (item: Array<Record<string, string>>) => {
                    let selectedColumns: string[] = [];

                    const currentItemType = item[0].hasOwnProperty('Type') ? item[0].Type : 'Incident';
                    const currentItemPlatform = item[0].hasOwnProperty('Platform') ? item[0].Platform : '';

                    selectedColumns = [
                        ...commonColumns,
                        ...mapPlatformTypeColumns(currentItemType, currentItemPlatform),
                    ];

                    const itemKeys = Object.keys(item[0]);
                    const newItem: Record<string, string> = {};

                    itemKeys.forEach((key) => {
                        if (selectedColumns.filter((column) => column === key).length > 0) {
                            newItem[key] = item[0][key];
                        }
                    });

                    return newItem;
                },
            );

            setArchiveMemoryDetails([...archiveMemoryWithSelectedColumns, ...contentWithJson]);
        } else if (contentWithJson.length > 0) {
            setArchiveMemoryDetails([...contentWithJson]);
        } else {
            setArchiveMemoryDetails([
                { NoDataMessage: "I'm sorry, but I can't find anything related to your question." },
            ]);
        }
    };

    React.useEffect(() => {
        if (message.source && message.content) {
            let contentWithJson: Array<Record<string, string>> = [];
            let textContent: string[] = [];
            let failedJsonItems = '';

            [contentWithJson, textContent, failedJsonItems] = utils.getJsonFromText(message.content) as [
                Array<Record<string, string>>,
                string[],
                string,
            ];

            const systemIdStrings = failedJsonItems.match(/(System\s*(id|ID|iD|Id)).*\n/g);

            if (systemIdStrings) {
                const systemIds = systemIdStrings.map((s) =>
                    s
                        .replace(/System\s*(id|ID|iD|Id)/, '')
                        .replace('\n', '')
                        .replace(/[^0-9a-zA-Z-]+/g, ''),
                );

                // eslint-disable-next-line @typescript-eslint/no-floating-promises
                getArchiveMemoryDetails(systemIds, contentWithJson);
            } else {
                if (
                    contentWithJson.length > 0 &&
                    contentWithJson[0].hasOwnProperty('System ID') &&
                    !contentWithJson[0]['System ID']
                ) {
                    setArchiveMemoryDetails([{ NoDataMessage: "I'm sorry, but I can't find anything." }]);
                } else {
                    setArchiveMemoryDetails(contentWithJson);
                }
            }

            setContentWithText(textContent);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [message.source]);

    let content: JSX.Element;

    if (isBot && message.type === ChatMessageType.Plan) {
        content = <PlanViewer message={message} messageIndex={messageIndex} getResponse={getResponse} />;
    } else if (message.type === ChatMessageType.Document) {
        content = <ChatHistoryDocumentContent isMe={isMe} message={message} />;
    } else {
        content =
            isBot && message.content.length === 0 ? <TypingIndicator /> : <ChatHistoryTextContent message={message} />;
    }

    // TODO: [Issue #42] Persistent RLHF, hook up to model
    // Currently for demonstration purposes only, no feedback is actually sent to kernel / model
    const showShowRLHFMessage =
        features[FeatureKeys.RLHF].enabled &&
        message.userFeedback === UserFeedback.Requested &&
        messageIndex === conversations[selectedId].messages.length - 1 &&
        message.userId === 'bot';

    const ifArchiveScope = message.source && (parseInt(message.source || '0') as ChatScope) === ChatScope.Archive;

    let platformTags: string[] = [];
    let typeTags: string[] = [];
    let groupedAttachments: IChatMessageGroupedAttachment[] = [];

    // Convert to HTML table
    if (isBot && message.content && ifArchiveScope && archiveMemoryDetails.length > 0) {
        if (!archiveMemoryDetails[0].hasOwnProperty('NoDataMessage')) {
            const platfromsFromJson = [...new Set(archiveMemoryDetails.map((item) => item.Platform))];
            const platforms =
                platfromsFromJson.length > 0
                    ? message.tags?.sources?.filter((s) => platfromsFromJson.filter((p) => p === s).length)
                    : message.tags?.sources;

            platformTags = (platforms && platforms.length > 0 ? platforms : platfromsFromJson).filter((t) => t);

            const typesFromJson = [...new Set(archiveMemoryDetails.map((item) => item.Type))];
            const types =
                typesFromJson.length > 0
                    ? message.tags?.types?.filter((s) => typesFromJson.filter((p) => p === s).length)
                    : message.tags?.types;

            typeTags = (types && types.length > 0 ? types : typesFromJson).filter((t) => t);

            groupedAttachments = (message.groupedAttachments ?? []).filter(
                (a) => archiveMemoryDetails.map((item) => item['System ID']).filter((p) => p === a.id).length,
            );
        }
    }

    const showDownloadReport =
        archiveMemoryDetails.length > 0 ? Object.keys(archiveMemoryDetails[0]).length > commonColumns.length : false;

    return (
        <div
            className={isMe ? mergeClasses(classes.root, classes.alignEnd) : classes.root}
            // The following data attributes are needed for CI and testing
            data-testid={`chat-history-item-${messageIndex}`}
            data-username={fullName}
            data-content={utils.formatChatTextContent(message)}
        >
            {
                <Persona
                    className={classes.persona}
                    avatar={avatar}
                    presence={
                        !features[FeatureKeys.SimplifiedExperience].enabled && !isMe
                            ? { status: 'available' }
                            : undefined
                    }
                />
            }
            <div className={isMe ? mergeClasses(classes.item, classes.me) : classes.item}>
                <div className={classes.header}>
                    {!isMe && <Text weight="semibold">{fullName}</Text>}
                    <Text className={classes.time}>{timestampToDateString(message.timestamp, true)}</Text>
                    <ChatContextMenu message={message} />
                </div>
                {archiveMemoryDetails.length > 0 ? (
                    <>
                        {contentWithText.length > 0 ? contentWithText[0].replace('[', '').replace('```json', '') : null}
                        <JsonTable jsonData={archiveMemoryDetails} showMoreDetails />
                        {contentWithText.length > 1 ? contentWithText[1].replace(']', '').replace('```', '') : null}
                    </>
                ) : (
                    content
                )}
                {showShowRLHFMessage && <UserFeedbackActions messageIndex={messageIndex} />}
                {isBot && message.extendedDetails?.exist && showDownloadReport && (
                    <ChatDownloadReport extendedDetails={message.extendedDetails} />
                )}
                {isBot &&
                    archiveMemoryDetails.length > 0 &&
                    message.tags &&
                    (platformTags.length > 0 || typeTags.length > 0) && (
                        <div className={classes.tagsContainer}>
                            {platformTags.map((tag, index) =>
                                tag ? (
                                    <div key={index} className={classes.tagWrapper}>
                                        <span className={mergeClasses(classes.pill, classes.highlight)}>
                                            <Link
                                                className={mergeClasses(classes.link, classes.noUrl)}
                                                key={index}
                                                title={tag}
                                            >
                                                {tag}
                                            </Link>
                                        </span>
                                    </div>
                                ) : null,
                            )}
                            {typeTags.map((tag, index) =>
                                tag ? (
                                    <div key={index} className={classes.tagWrapper}>
                                        <span className={classes.pill}>
                                            <Link
                                                className={mergeClasses(classes.link, classes.noUrl)}
                                                key={index}
                                                title={tag}
                                            >
                                                {tag}
                                            </Link>
                                        </span>
                                    </div>
                                ) : null,
                            )}
                        </div>
                    )}
                {isBot &&
                    archiveMemoryDetails.length > 0 &&
                    groupedAttachments.length > 0 &&
                    groupedAttachments.filter((a) => a.url).length > 0 && (
                        <GroupedAttachments attachments={groupedAttachments} />
                    )}
                {isBot && message.citations && (
                    <Citations citations={utils.getUsedCitations(message)} isAttachments={!!ifArchiveScope} />
                )}
            </div>
            {showShowRLHFMessage && message.userFeedback === UserFeedback.Positive && (
                <ThumbLike16Filled color="gray" />
            )}
            {showShowRLHFMessage && message.userFeedback === UserFeedback.Negative && (
                <ThumbDislike24Filled color="gray" />
            )}
        </div>
    );
};
